import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Scan, Share2, Copy, Check, ExternalLink, AlertCircle, ArrowRight, Shield, Twitter, Facebook, MessageCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VerifiedBadge from "../components/VerifiedBadge";
import { http } from "../lib/api";

// Light-weight outlet name extraction from the URL host so the verified-source
// badge can render even when the backend doesn't echo a source name.
function outletFromUrl(url) {
  if (!url) return "";
  try {
    const host = new URL(url).hostname.replace(/^www\./, "").replace(/^m\./, "");
    // Try the second-level domain as the outlet hint ("reuters.com" → "Reuters")
    const root = host.split(".")[0];
    if (!root) return host;
    // Common aliases: news-prefixed hosts ("news.cnn.com" → "CNN")
    const COMMON = {
      reuters: "Reuters", apnews: "Associated Press", cnn: "CNN", foxnews: "Fox News",
      nytimes: "New York Times", wsj: "Wall Street Journal", washingtonpost: "Washington Post",
      bbc: "BBC", nbcnews: "NBC News", cbsnews: "CBS News", abcnews: "ABC News",
      npr: "NPR", bloomberg: "Bloomberg", theatlantic: "The Atlantic", economist: "The Economist",
      wired: "Wired", theverge: "The Verge", arstechnica: "Ars Technica", techcrunch: "TechCrunch",
      ft: "Financial Times", theguardian: "The Guardian", politico: "Politico",
      axios: "Axios", thehill: "The Hill", washingtonexaminer: "Washington Examiner",
      washingtontimes: "Washington Times", usatoday: "USA Today",
    };
    return COMMON[root] || root[0].toUpperCase() + root.slice(1);
  } catch {
    return "";
  }
}

const MEDIA_LABELS = {
  twitter: "X / Twitter", youtube: "YouTube", reddit: "Reddit", tiktok: "TikTok",
  facebook: "Facebook", instagram: "Instagram", threads: "Threads", bluesky: "Bluesky",
  linkedin: "LinkedIn", podcast: "Podcast", substack: "Substack", blog: "Blog",
  news: "News article", other: "Web page", text: "Text",
};

/**
 * Public score detail page — anyone can land here from a shared link.
 * Shows the rendered score card image (same one social previews use) and the
 * full Newseze narrative + scores. Includes a prominent "Score your own"
 * CTA — every shared link is an acquisition surface for new readers.
 */
export default function ScoredShare() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const ogUrl = `${apiBase}/api/scored/${id}/og.png`;
  const shareUrl = `${apiBase}/api/share/scored/${id}`;

  useEffect(() => {
    let alive = true;
    http.get(`/scored/${id}`)
      .then((r) => alive && setDoc(r.data))
      .catch((ex) => alive && setErr(ex?.response?.data?.detail || "Score not found"));
    return () => { alive = false; };
  }, [id]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* clipboard blocked */ }
  };

  if (err) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="max-w-[760px] mx-auto px-5 py-16 text-center">
          <AlertCircle size={36} className="text-rose-500 mx-auto mb-3" />
          <h1 className="text-[24px] font-black mb-2">Score not found</h1>
          <p className="text-[14px] text-[var(--muted)] mb-6">This score may have been removed or the link is invalid.</p>
          <Link to="/" className="btn primary"><ArrowRight size={14} /> Back to Newseze</Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="max-w-[760px] mx-auto px-5 py-16 text-center text-[var(--muted)]">Loading score…</main>
        <Footer />
      </div>
    );
  }

  const mediaLabel = MEDIA_LABELS[doc.media_type] || "Web page";

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-[1080px] mx-auto px-5 py-6" data-testid="scored-share-page">

        {/* Score card image (the same one social previews use) */}
        <div className="rounded-2xl overflow-hidden shadow-2xl border border-[var(--gold)] bg-[var(--ink)] mb-6">
          <img src={ogUrl} alt="Newseze score card" className="w-full h-auto block" data-testid="scored-og-image" />
        </div>

        {/* Headline + media tag */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--gold)]">Newseze Universal Scorer</div>
              <span className="text-[9.5px] uppercase tracking-[0.14em] font-black bg-[#fff9ed] text-[#7a5b00] border border-[var(--gold)] px-1.5 py-0.5 rounded">
                {mediaLabel}
              </span>
            </div>
            <h1 className="text-[26px] sm:text-[34px] font-black tracking-[-0.025em] leading-tight" data-testid="scored-title">
              {doc.newseze_title || doc.input_title}
            </h1>
          </div>

          {/* Share buttons */}
          <div className="flex items-center gap-1.5" data-testid="scored-share-buttons">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("Newseze scored this: Trust " + doc.trust_score + " · Drama " + doc.drama_score)}&url=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
              className="btn icon" title="Share on X" data-testid="scored-share-x"
            >
              <Twitter size={13} /> X
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
              className="btn icon" title="Share on Facebook" data-testid="scored-share-fb"
            >
              <Facebook size={13} /> FB
            </a>
            <a
              href={`https://wa.me/?text=${encodeURIComponent("Newseze scored this: " + shareUrl)}`}
              target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
              className="btn icon" title="Share on WhatsApp" data-testid="scored-share-wa"
            >
              <MessageCircle size={13} /> WA
            </a>
            <button onClick={copy} className="btn icon" data-testid="scored-share-copy">
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
        </div>

        {/* Why it matters + narrative */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5 mb-8">
          <article>
            {doc.blocked && (
              <div className="rounded-md bg-rose-50 border border-rose-200 p-3 mb-3 text-[13px] text-rose-800 flex gap-2 items-start" data-testid="scored-blocked">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <div>
                  <b>Newseze would not republish this.</b>
                  <div>{doc.block_reason || "Failed calm filters."}</div>
                </div>
              </div>
            )}
            {doc.why_it_matters && (
              <p className="text-[15px] font-bold text-[var(--ink)] mb-4 leading-snug border-l-4 border-[var(--gold)] pl-3 italic">{doc.why_it_matters}</p>
            )}
            <p className="text-[14px] text-[var(--ink)] leading-relaxed whitespace-pre-wrap" data-testid="scored-narrative">{doc.narrative}</p>
            {doc.source_url && (
              <a href={doc.source_url} target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer" className="mt-4 inline-flex items-center gap-1.5 text-[12px] text-[var(--blue)] underline break-all">
                <ExternalLink size={12} /> Original source
              </a>
            )}
          </article>

          <aside className="space-y-4">
            <div className="rounded-xl border border-[var(--line)] bg-white p-5">
              <div className="flex items-center gap-2 mb-3"><Shield size={14} className="text-[var(--gold)]" /><div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)]">Editorial Scores</div></div>
              <ScoreRow label="Trust" value={doc.trust_score} good />
              <ScoreRow label="Respect" value={doc.respect_score} good />
              <ScoreRow label="Drama" value={doc.drama_score} good={false} />
              <ScoreRow label="Conspiracy" value={doc.conspiracy_score} good={false} />
              <div className="mt-3 pt-3 border-t border-[var(--line)]">
                <div className="text-[10.5px] uppercase tracking-[0.16em] font-black text-[var(--muted)]">Bias Score</div>
                <div className="text-[20px] font-black tabular-nums">{doc.bias_score > 0 ? `+${doc.bias_score}` : doc.bias_score}</div>
                <div className="text-[10px] text-[var(--muted)] italic">-100 left · 0 center · +100 right</div>
              </div>
              {typeof doc.views === "number" && (
                <div className="mt-3 pt-3 border-t border-[var(--line)] text-[10.5px] text-[var(--muted)]">
                  {doc.views.toLocaleString()} {doc.views === 1 ? "view" : "views"}
                </div>
              )}
            </div>

            {/* Sources & Attribution — journalistic transparency block */}
            <div className="rounded-xl border-2 border-[var(--gold)] bg-[#fff9ed] p-5" data-testid="scored-sources">
              <div className="flex items-center gap-2 mb-3">
                <ExternalLink size={14} className="text-[var(--gold)]" />
                <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[#7a5b00]">Sources &amp; Attribution</div>
              </div>
              {doc.source_url ? (
                <>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] font-black text-[var(--muted)] mb-1 flex items-center gap-1.5">
                    Primary source
                    {(() => {
                      const outlet = outletFromUrl(doc.source_url);
                      return outlet ? <VerifiedBadge name={outlet} testId="scored-source-verified" /> : null;
                    })()}
                  </div>
                  <a
                    href={doc.source_url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    referrerPolicy="no-referrer"
                    className="block text-[12.5px] font-bold text-[var(--blue)] underline break-all hover:text-[var(--ink)] mb-3"
                    data-testid="scored-source-url"
                  >
                    {doc.source_url}
                  </a>
                </>
              ) : (
                <div className="text-[12px] text-[var(--muted)] italic mb-3" data-testid="scored-source-none">
                  No external URL — content was pasted directly or from a screenshot. Newseze&apos;s analysis is original commentary based on the user-supplied input.
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-[11px] mb-3">
                <div>
                  <div className="text-[9.5px] uppercase tracking-[0.14em] font-black text-[var(--muted)]">Format</div>
                  <div className="font-bold">{mediaLabel}</div>
                </div>
                <div>
                  <div className="text-[9.5px] uppercase tracking-[0.14em] font-black text-[var(--muted)]">Scored</div>
                  <div className="font-bold">{doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "—"}</div>
                </div>
              </div>
              <div className="text-[10.5px] text-[#5a4500] leading-snug">
                Newseze did not interview anyone or independently verify the source&apos;s reporting.
                The analysis above is editorial commentary applying the Newseze framework to the
                source&apos;s claims under fair use.
              </div>
            </div>

            {/* Report / Right-of-reply block — defamation defense */}
            <div className="rounded-xl border border-[var(--line)] bg-white p-5" data-testid="scored-correction">
              <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-2">Right of reply &amp; corrections</div>
              <p className="text-[12px] text-[var(--muted)] mb-3 leading-snug">
                Are you the subject of this story or the original publisher? Newseze respects
                right-of-reply and corrects factual errors promptly.
              </p>
              <div className="flex flex-col gap-1.5">
                <a
                  href={`mailto:corrections@newseze.com?subject=${encodeURIComponent("Correction request — score " + id)}&body=${encodeURIComponent("Score URL: " + (typeof window !== "undefined" ? window.location.href : "") + "\n\nWhat should be corrected:\n")}`}
                  className="btn icon text-[11.5px]"
                  data-testid="scored-correction-email"
                >
                  Request correction
                </a>
                <Link to="/legal" className="btn icon text-[11.5px]" data-testid="scored-correction-dmca">
                  File DMCA / takedown
                </Link>
              </div>
            </div>
          </aside>
        </div>

        {/* CTA — "Score your own" */}
        <div className="rounded-2xl border-2 border-[var(--gold)] bg-gradient-to-br from-[#0a1f3d] to-[#1e3a8a] text-white p-6 sm:p-8 text-center" data-testid="scored-cta">
          <div className="text-[11px] uppercase tracking-[0.2em] font-black text-[var(--gold)] mb-2 inline-flex items-center gap-2">
            <Scan size={13} /> Newseze Universal Scorer
          </div>
          <h2 className="text-[26px] sm:text-[32px] font-black tracking-[-0.02em] leading-tight mb-2">Score any URL in 10 seconds.</h2>
          <p className="text-[13.5px] text-white/80 mb-5 max-w-[560px] mx-auto">
            Paste any X post, YouTube video, Reddit thread, or news article. Newseze returns a calm,
            factual score — trust, drama, conspiracy, respect, bias. No drama. No conspiracies.
          </p>
          <Link to="/reader-tools?tab=paste" className="btn gold inline-flex items-center gap-2" data-testid="scored-cta-btn">
            <Scan size={14} /> Try Newseze free <ArrowRight size={14} />
          </Link>
        </div>

        {/* Legal / fair-use notice */}
        <div className="mt-6 rounded-lg border border-[var(--line)] bg-white p-4 text-[11.5px] text-[var(--muted)] leading-snug" data-testid="scored-legal">
          <b className="text-[var(--ink)] block mb-1">Editorial &amp; fair-use notice</b>
          Newseze provides commentary and analysis under fair use (17 U.S.C. § 107). Factual claims
          belong to the original source and are attributed accordingly. Newseze scores reflect our
          editorial framework — not statements of fact about any person, party, or organization.
          Not financial, legal, medical, or tax advice. Right-holders may request takedown via our{" "}
          <Link to="/legal" className="text-[var(--blue)] underline">DMCA form</Link>.
        </div>

      </main>
      <Footer />
    </div>
  );
}

function ScoreRow({ label, value, good = true }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const color = good ? (pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-rose-500") : (pct >= 70 ? "bg-rose-500" : pct >= 40 ? "bg-amber-500" : "bg-emerald-500");
  return (
    <div className="mb-2">
      <div className="flex justify-between text-[11px]"><span className="text-[var(--muted)]">{label}</span><b className="tabular-nums">{value || 0}</b></div>
      <div className="h-1.5 bg-[var(--bg)] rounded-full overflow-hidden mt-0.5"><div className={`h-full ${color}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}
